import { Pipe, PipeTransform } from '@angular/core';
import { AbstractSpaceClass } from '../../../../../models/space/abstract-space.class';
import { OverlayField } from './details-overlay.model';
import { formatNumbers, isNotNullOrEmptyString } from '../../../../../shared/utils/functions';
import { L10nTranslationService } from 'angular-l10n';
import { DASH, areaUomAbbrev } from '@miq/utils';
import { StackPlanViewStateService } from '../../../../../state/sp-view-state.service';

@Pipe({
  name: 'dataFormatter',
  standalone: false,
})
export class DataFormatterPipe implements PipeTransform {
  constructor(
    private translationService: L10nTranslationService,
    private viewStateService: StackPlanViewStateService
  ) {}

  transform(
    space: AbstractSpaceClass,
    subsetConfig: Pick<OverlayField, 'displayFn' | 'inputType' | 'unit' | 'values' | 'isAreaField' | 'rawValueFn'>,
    options?: { defaultTxt: string; label?: string; rawData?: boolean }
  ): string {
    const column = this.getColumnById(subsetConfig['id'] as number);
    const valueToDisplay = this.#getValueToDisplay(space, subsetConfig, options, column);
    const { defaultTxt = '-', label = null } = options || {};
    const { inputType } = subsetConfig;
    let result: any | { [attr: string]: any } = defaultTxt;
    if (['string', 'number'].includes(typeof valueToDisplay) && isNotNullOrEmptyString(valueToDisplay)) {
      let digitalInfo;
      if (column?.options?.format) {
        digitalInfo = column.options.format;
      } else {
        digitalInfo = this.getDigitalInfo(inputType);
      }

      result =
        typeof valueToDisplay === 'string'
          ? valueToDisplay.toString()
          : formatNumbers(valueToDisplay.toString(), digitalInfo);
    } else if (valueToDisplay != null && label != null && label !== 'test') {
      result = this.translationService.translate(label, valueToDisplay);
    } else if (!!valueToDisplay || valueToDisplay === false) {
      result = valueToDisplay;
    }

    return result;
  }

  #getValueToDisplay(space, subsetConfig, options, column) {
    const { rawData = false } = options || {};
    const { displayFn, rawValueFn, unit, values, isAreaField } = subsetConfig;
    let value;

    if (rawData && rawValueFn) {
      value = rawValueFn(space);
    } else {
      value = displayFn
        ? values && !rawData
          ? this.getValue(values, displayFn(space, column))
          : displayFn(space, column)
        : null;
    }

    const unitVal = unit ? this.getValue(unit?.values, unit?.displayFn(space, column)) : null;
    const abbrevUOMValue = isAreaField ? areaUomAbbrev(unitVal) : unitVal;
    if (abbrevUOMValue && !rawData) {
      return value != null && unitVal != null ? `${value} ${abbrevUOMValue}` : DASH;
    }
    return value;
  }

  private getValue(values, key): string {
    const found = values.find(uom => uom.key === key);
    return found ? found.key : key;
  }

  private getColumnById(columnId: number) {
    if (!columnId) {
      return null;
    }
    const columns = this.viewStateService.getColumns();
    return columns?.find(col => col.columnId === columnId);
  }

  private getDigitalInfo(inputType: string): string {
    const digitalInfoMap: Record<string, string> = {
      numeric: '.0-2',
      currency: '.2-2',
    };
    return digitalInfoMap[inputType] ?? '.0-0';
  }
}
